/**
 * Image compression utility for client-side image optimization
 * Compresses and resizes images before upload to reduce payload size
 */

/**
 * Compresses an image file using Canvas API
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 4)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.85,
    maxSizeMB = 4
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            // Check if compressed size is acceptable
            const sizeMB = blob.size / (1024 * 1024);
            
            // If still too large, compress more aggressively
            if (sizeMB > maxSizeMB) {
              // Try with lower quality
              canvas.toBlob(
                (smallerBlob) => {
                  if (!smallerBlob) {
                    // If compression fails, return original blob
                    resolve(new File([blob], file.name, { type: file.type }));
                    return;
                  }
                  
                  const smallerSizeMB = smallerBlob.size / (1024 * 1024);
                  
                  // If still too large, try even lower quality
                  if (smallerSizeMB > maxSizeMB) {
                    canvas.toBlob(
                      (smallestBlob) => {
                        if (!smallestBlob) {
                          resolve(new File([smallerBlob], file.name, { type: file.type }));
                          return;
                        }
                        resolve(new File([smallestBlob], file.name, { type: file.type }));
                      },
                      file.type || 'image/jpeg',
                      0.7 // Even lower quality
                    );
                  } else {
                    resolve(new File([smallerBlob], file.name, { type: file.type }));
                  }
                },
                file.type || 'image/jpeg',
                0.75 // Lower quality
              );
            } else {
              resolve(new File([blob], file.name, { type: file.type }));
            }
          },
          file.type || 'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Gets image dimensions without loading full image
 * @param {File} file - The image file
 * @returns {Promise<{width: number, height: number}>}
 */
export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Estimates compressed size without actually compressing
 * @param {File} file - The image file
 * @param {Object} options - Compression options
 * @returns {Promise<number>} - Estimated size in bytes
 */
export const estimateCompressedSize = async (file, options = {}) => {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.85
  } = options;
  
  const dimensions = await getImageDimensions(file);
  
  // Calculate new dimensions
  let width = dimensions.width;
  let height = dimensions.height;
  
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = width * ratio;
    height = height * ratio;
  }
  
  // Rough estimate: width * height * quality * bytes per pixel
  // JPEG is roughly 0.5-2 bytes per pixel depending on quality and content
  const estimatedBytesPerPixel = quality * 1.5;
  const estimatedSize = width * height * estimatedBytesPerPixel;
  
  return Math.min(estimatedSize, file.size); // Don't estimate larger than original
};

